// components/HowWeWorkSection.js
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Container } from '../styles/StyledComponents';

const SectionWrapper = styled.section`
  padding: 120px 2rem 140px;        // matches other sections exactly
  background: #000000;
  position: relative;
  border-top: 1px solid rgba(255, 255, 255, 0.2);   // elegant top border

  @media (max-width: 768px) {
    padding: 5rem 1rem 6rem;
  }

  @media (max-width: 480px) {
    padding: 60px 2rem;
  }
`;

const Content = styled.div`
  max-width: 1600px;
  margin: 0 auto;
`;

// Title + hero statement stacked vertically (no square/line)
const Title = styled(motion.h2)`
  font-size: 28px;
  color: #ffffff;
  margin: 0 0 1.5rem 0;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-align: left;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const HeroStatement = styled(motion.h1)`
  font-size: clamp(3rem, 8vw, 2.5rem);
  font-weight: 700;
  line-height: 1.02;
  letter-spacing: -2.5px;
  color: #ffffff;
  margin: 0 0 6rem 0;

  @media (max-width: 1024px) {
    font-size: 66px;
  }
  @media (max-width: 768px) {
    font-size: 48px;
    margin-bottom: 4rem;
  }
`;

// 3-column grid
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
  }
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3.5rem;
  }
`;

const Column = styled(motion.div)`
  display: flex;
  flex-direction: column;
`;

const Number = styled.div`
  font-size: 120px;
  font-weight: 700;
  line-height: 0.9;
  color: transparent;
  -webkit-text-stroke: 1.5px rgba(255, 255, 255, 0.37);
  margin-bottom: 1rem;
  user-select: none;

  @media (max-width: 768px) {
    font-size: 100px;
  }
`;

const Text = styled(motion.p)`
  font-size: 21px;
  font-weight: 500;
  line-height: 1.35;
  color: #ffffff;
  margin: 0;

  @media (max-width: 1024px) {
    font-size: 26px;
  }
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const HowWeWorkSection = () => {
  const statements = [
    "Partner with C-suite, Senior Leadership and Human Resources and transformational talent",
    "Collaborate to ensure the best talent is matched to the organization and vice versa.",
    "Commitment and care, providing exceptional executive recruitment services to both clients and candidates.",
    "Proven track record — our hires generally stay for a minimum of three years and have always progressed.",
    "Innovate — we have built teams behind some of the most respected brands and organizations in the world."
  ];

  return (
    <SectionWrapper id="how-we-work">
      <Container>
        <Content>
          {/* Title + Hero stacked */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <Title>How We Work</Title>
            <HeroStatement>
              Transforming recruitment<br />into lasting impact
            </HeroStatement>
          </motion.div>

          {/* 3-column grid */}
          <Grid>
            {statements.map((text, i) => (
              <Column
                key={i}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: i * 0.15 }}
                viewport={{ once: true }}
              >
                <Number>0{i + 1}</Number>
                <Text>{text}</Text>
              </Column>
            ))}
          </Grid>
        </Content>
      </Container>
    </SectionWrapper>
  );
};

export default HowWeWorkSection;